import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { mergeClasses, Popover, PopoverSurface } from '@fluentui/react-components';
import { ChevronRight20Regular } from '@fluentui/react-icons';
import { NavRailItem } from '@components/common/NavRailItem';
import { NavRailSubItem } from '@components/common/NavRailSubItem';
import { useRailVisibleItems } from '@/hooks/useRailVisibleItems';
import { useIsMobile } from '@/hooks/useIsMobile';
import { telemetry } from '@services/telemetry';
import { TELEMETRY_COMPONENT, EVENT_TYPES } from '@/constants';
import type {
  RailItem,
  RailItemKey,
  RailItemViewModel,
  RailState,
  RailSubItem,
} from '@/types/leftNavRail.types';
import { useLeftNavRailStyles } from './LeftNavRail.styles';

export interface LeftNavRailProps {
  readonly railState: RailState;
}

interface RailParentRowProps {
  readonly vm: RailItemViewModel;
  readonly railState: RailState;
  readonly onTopLevelSelect: (item: RailItem) => void;
  readonly onSubItemSelect: (parent: RailItem, sub: RailSubItem) => void;
  readonly onToggleGroup: (key: RailItemKey) => void;
  readonly onFlyoutOpen: (parent: RailItem) => void;
}

function RailParentRow({
  vm,
  railState,
  onTopLevelSelect,
  onSubItemSelect,
  onToggleGroup,
  onFlyoutOpen,
}: RailParentRowProps) {
  const styles = useLeftNavRailStyles();
  const { t } = useTranslation();
  const isExpanded = railState === 'expanded';

  // Controlled open state for the collapsed-rail flyout so it can be opened by
  // keyboard focus in addition to hover (WCAG 2.1.1). openRef guards the
  // telemetry side-effect so it fires only on a real closed -> open transition.
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const openRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const setFlyoutOpenState = useCallback(
    (next: boolean) => {
      if (next && !openRef.current) onFlyoutOpen(vm.item);
      openRef.current = next;
      setFlyoutOpen(next);
    },
    [onFlyoutOpen, vm.item]
  );

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current !== undefined) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
  }, []);

  const openFlyout = useCallback(() => {
    cancelScheduledClose();
    setFlyoutOpenState(true);
  }, [cancelScheduledClose, setFlyoutOpenState]);

  const scheduleCloseFlyout = useCallback(() => {
    cancelScheduledClose();
    closeTimerRef.current = setTimeout(() => setFlyoutOpenState(false), 150);
  }, [cancelScheduledClose, setFlyoutOpenState]);

  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  const handleFlyoutBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      // The surface is portaled, so check both the trigger container and the
      // surface; keep the flyout open while focus stays within either.
      const next = event.relatedTarget as Node | null;
      if (containerRef.current?.contains(next) || surfaceRef.current?.contains(next)) {
        return;
      }
      setFlyoutOpenState(false);
    },
    [setFlyoutOpenState]
  );

  const pendingFocusRef = useRef<'first' | 'last' | null>(null);

  const getMenuItems = useCallback(
    () => Array.from(surfaceRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []),
    []
  );

  const focusTrigger = useCallback(() => {
    triggerRef.current?.querySelector<HTMLElement>('a, button')?.focus();
  }, []);

  useEffect(() => {
    if (!flyoutOpen || pendingFocusRef.current === null) return;
    const items = getMenuItems();
    const target = pendingFocusRef.current === 'last' ? items[items.length - 1] : items[0];
    pendingFocusRef.current = null;
    target?.focus();
  }, [flyoutOpen, getMenuItems]);

  const handleFlyoutKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape') {
        if (flyoutOpen) {
          event.preventDefault();
          setFlyoutOpenState(false);
          focusTrigger();
        }
        return;
      }
      if (
        event.key !== 'ArrowDown' &&
        event.key !== 'ArrowUp' &&
        event.key !== 'Home' &&
        event.key !== 'End'
      ) {
        return;
      }
      event.preventDefault();
      const items = getMenuItems();
      if (!flyoutOpen || items.length === 0) {
        pendingFocusRef.current = event.key === 'ArrowUp' || event.key === 'End' ? 'last' : 'first';
        openFlyout();
        return;
      }
      const current = items.indexOf(document.activeElement as HTMLElement);
      let nextIndex: number;
      switch (event.key) {
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = items.length - 1;
          break;
        case 'ArrowUp':
          nextIndex = current <= 0 ? items.length - 1 : current - 1;
          break;
        default:
          nextIndex = current < 0 || current === items.length - 1 ? 0 : current + 1;
          break;
      }
      items[nextIndex]?.focus();
    },
    [flyoutOpen, getMenuItems, focusTrigger, openFlyout, setFlyoutOpenState]
  );

  const handleChevronClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onToggleGroup(vm.item.key);
    },
    [onToggleGroup, vm.item.key]
  );

  const handleChevronKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggleGroup(vm.item.key);
      }
    },
    [onToggleGroup, vm.item.key]
  );

  const handleParentClick = useCallback(
    (item: RailItem, event: MouseEvent | KeyboardEvent) => {
      if (isExpanded && vm.visibleChildren.length > 0) {
        event.preventDefault();
        onToggleGroup(vm.item.key);
      } else {
        onTopLevelSelect(item);
      }
    },
    [isExpanded, vm.visibleChildren.length, vm.item.key, onToggleGroup, onTopLevelSelect]
  );

  const parentRow = (
    <div className={styles.parentRow}>
      <div className={styles.parentRowItem}>
        <NavRailItem
          item={vm.item}
          isActive={vm.isActive}
          railState={railState}
          onSelect={handleParentClick}
          disableCollapsedTooltip
        />
      </div>
      {isExpanded && (
        <button
          type="button"
          className={mergeClasses(styles.chevron, vm.isGroupExpanded && styles.chevronOpen)}
          onClick={handleChevronClick}
          onKeyDown={handleChevronKeyDown}
          aria-expanded={vm.isGroupExpanded}
          aria-controls={`rail-group-${vm.item.key}`}
          aria-label={t(vm.isGroupExpanded ? 'leftNav.collapseGroup' : 'leftNav.expandGroup', {
            item: t(vm.item.labelKey),
          })}
          data-testid={`rail-item-${vm.item.key}-chevron`}
        >
          <ChevronRight20Regular />
        </button>
      )}
    </div>
  );

  if (isExpanded) {
    return (
      <>
        {parentRow}
        <ul
          id={`rail-group-${vm.item.key}`}
          className={mergeClasses(
            styles.nestedList,
            vm.isGroupExpanded ? styles.nestedListOpen : styles.nestedListClosed
          )}
          aria-hidden={!vm.isGroupExpanded}
          inert={!vm.isGroupExpanded}
        >
          {vm.visibleChildren.map((sub) => (
            <li key={sub.key}>
              <NavRailSubItem
                subItem={sub}
                isActive={vm.activeChildKey === sub.key}
                onSelect={(s) => onSubItemSelect(vm.item, s)}
              />
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <div ref={containerRef} className={styles.flyoutContainer} onBlur={handleFlyoutBlur}>
      <div
        ref={triggerRef}
        data-testid={`rail-item-${vm.item.key}-flyout-trigger`}
        onMouseEnter={openFlyout}
        onMouseLeave={scheduleCloseFlyout}
      >
        <div className={styles.parentRow}>
          <div className={styles.parentRowItem}>
            <NavRailItem
              item={vm.item}
              isActive={vm.isActive}
              railState={railState}
              onSelect={(item) => onTopLevelSelect(item)}
              onKeyDown={handleFlyoutKeyDown}
              disableCollapsedTooltip
            />
          </div>
        </div>
      </div>
      <Popover
        open={flyoutOpen}
        positioning={{ target: triggerRef.current, position: 'after', align: 'top' }}
        onOpenChange={(_event, data) => {
          setFlyoutOpenState(data.open);
        }}
      >
        <PopoverSurface
          ref={surfaceRef}
          className={styles.popoverSurface}
          data-testid={`rail-item-${vm.item.key}-flyout`}
          role="menu"
          aria-label={t(vm.item.labelKey)}
          onMouseEnter={openFlyout}
          onMouseLeave={scheduleCloseFlyout}
          onKeyDown={handleFlyoutKeyDown}
        >
          <ul className={styles.flyoutList}>
            {vm.visibleChildren.map((sub) => (
              <li key={sub.key}>
                <NavRailSubItem
                  subItem={sub}
                  isActive={vm.activeChildKey === sub.key}
                  onSelect={(s) => onSubItemSelect(vm.item, s)}
                  role="menuitem"
                />
              </li>
            ))}
          </ul>
        </PopoverSurface>
      </Popover>
    </div>
  );
}

export const LeftNavRail = ({ railState }: LeftNavRailProps) => {
  const styles = useLeftNavRailStyles();
  const { t } = useTranslation();
  const { items, toggleGroup } = useRailVisibleItems();
  const isMobile = useIsMobile();

  const handleTopLevelSelect = useCallback((item: RailItem) => {
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.NAVIGATION, {
      source: 'leftNavRail',
      itemKey: item.key,
      target: item.defaultRoute,
    });
  }, []);

  const handleSubItemSelect = useCallback((parent: RailItem, sub: RailSubItem) => {
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.NAVIGATION, {
      source: `leftNavRail.${parent.key}`,
      sectionKey: sub.key,
      target: sub.route,
    });
  }, []);

  const handleFlyoutOpen = useCallback((parent: RailItem) => {
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.UI_INTERACTION, {
      source: `leftNavRail.${parent.key}.flyout`,
      action: 'open',
    });
  }, []);

  if (isMobile) return null;

  return (
    <nav
      className={mergeClasses(
        styles.root,
        railState === 'expanded' ? styles.rootExpanded : styles.rootCollapsed
      )}
      role="navigation"
      aria-label={t('leftNav.navigationLabel')}
      data-testid="left-nav-rail"
      data-state={railState}
      id="left-nav-rail"
    >
      <ul className={styles.list}>
        {items.map((vm) => (
          <li key={vm.item.key} className={styles.listItem}>
            {vm.visibleChildren.length > 0 ? (
              <RailParentRow
                vm={vm}
                railState={railState}
                onTopLevelSelect={handleTopLevelSelect}
                onSubItemSelect={handleSubItemSelect}
                onToggleGroup={toggleGroup}
                onFlyoutOpen={handleFlyoutOpen}
              />
            ) : (
              <NavRailItem
                item={vm.item}
                isActive={vm.isActive}
                railState={railState}
                onSelect={(item) => handleTopLevelSelect(item)}
              />
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};

LeftNavRail.displayName = 'LeftNavRail';
